import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, setDoc, getDoc, onSnapshot, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseInstance';
import { getAppId } from '../firebaseConfig';

const appId = getAppId();

// Default data structure
const defaultProfileData = {
  name: "",
  tagline: "",
  username: "",
  avatar: "",
  bio: "",
  profession: "",
  role: "Creator",
  tone: "",
  formattingPreference: "casual",
  emojiStyle: "aesthetic",
  userGoal: "",
  theme: "midnight",
  spotlight: { title: "", subtitle: "", url: "", buttonText: "", image: null }, 
  showcases: [],
  bookingUrl: "",
  showMemories: true,
  showBusiness: true,
  showSpotlightAndListings: false,
  showBooking: false,
  showContactInfo: false,
  enableSubscriptions: false,
  showSubscriberCount: true,
  links: [],
  knowledge: [],
  qna: [],
  chatPrompts: [],
  chatIntroText: "Trained by [Name] • Same vibe, different dimension ✨",
  chatButtonSubtext: "CONNECT INSTANTLY",
  chatButtonText: "Say hello 👋",
  showChatbotOnProfile: false,
  socialIconStyle: "neutral",
  contactInfo: {
    phones: [],
    emails: []
  },
  media: []
};

// Optimized: Fast hash function for data comparison (faster than JSON.stringify but still accurate)
const fastHash = (obj) => {
  // Hash based on actual content, not just lengths
  // This is faster than JSON.stringify but still detects real changes
  let hash = 0;
  const keys = Object.keys(obj).sort();
  for (const key of keys) {
    const value = obj[key];
    // Include key in hash
    for (let i = 0; i < key.length; i++) {
      hash = ((hash << 5) - hash) + key.charCodeAt(i);
      hash = hash & hash;
    }
    
    if (value === null || value === undefined) {
      hash = ((hash << 5) - hash) + 0;
    } else if (Array.isArray(value)) {
      // Hash array length and first few items for speed
      hash = ((hash << 5) - hash) + value.length;
      for (let i = 0; i < Math.min(value.length, 3); i++) {
        const item = value[i];
        if (typeof item === 'string') {
          hash = ((hash << 5) - hash) + item.length + (item.charCodeAt(0) || 0);
        } else if (typeof item === 'object' && item !== null) {
          hash = ((hash << 5) - hash) + Object.keys(item).length;
        }
        hash = hash & hash;
      }
    } else if (value && typeof value === 'object') {
      // For objects, hash key count and a sample of values
      const objKeys = Object.keys(value);
      hash = ((hash << 5) - hash) + objKeys.length;
      for (let i = 0; i < Math.min(objKeys.length, 3); i++) {
        const objKey = objKeys[i];
        const objValue = value[objKey];
        if (typeof objValue === 'string') {
          hash = ((hash << 5) - hash) + objValue.length + (objValue.charCodeAt(0) || 0);
        } else {
          hash = ((hash << 5) - hash) + String(objValue).length;
        }
        hash = hash & hash;
      }
    } else {
      // For primitives, hash the actual value
      const str = String(value);
      hash = ((hash << 5) - hash) + str.length;
      // Include first and last character for better collision detection
      if (str.length > 0) {
        hash = ((hash << 5) - hash) + str.charCodeAt(0);
        if (str.length > 1) {
          hash = ((hash << 5) - hash) + str.charCodeAt(str.length - 1);
        }
      }
    }
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash;
};

// Extract data merging logic to avoid duplication
const mergeProfileData = (data) => {
  const qnaValue = Array.isArray(data.qna) ? data.qna : [];
  
  // DEFENSIVE: Fix showcases/spotlight structure if corrupted
  let fixedShowcases = [];
  let fixedSpotlight = defaultProfileData.spotlight;
  
  if (Array.isArray(data.showcases)) {
    fixedShowcases = data.showcases;
  } else if (data.showcases && typeof data.showcases === 'object') {
    const corrupted = data.showcases;
    if (corrupted.spotlight) {
      fixedSpotlight = corrupted.spotlight;
    }
    const { spotlight, ...showcaseData } = corrupted;
    if (showcaseData.title || showcaseData.url || showcaseData.image || 
        showcaseData.subtitle || showcaseData.buttonText) {
      fixedShowcases = [showcaseData];
    }
  }
  
  if (data.spotlight && typeof data.spotlight === 'object') {
    fixedSpotlight = data.spotlight;
  }
  
  return { 
    ...defaultProfileData,
    ...data,
    chatButtonSubtext: data.chatButtonSubtext !== undefined ? data.chatButtonSubtext : defaultProfileData.chatButtonSubtext,
    chatButtonText: data.chatButtonText !== undefined ? data.chatButtonText : defaultProfileData.chatButtonText,
    socialIconStyle: data.socialIconStyle !== undefined ? data.socialIconStyle : defaultProfileData.socialIconStyle,
    profession: data.profession || data.role || '',
    formattingPreference: data.formattingPreference !== undefined ? data.formattingPreference : defaultProfileData.formattingPreference,
    emojiStyle: data.emojiStyle !== undefined ? data.emojiStyle : (data.formattingPreference || defaultProfileData.emojiStyle),
    userGoal: data.userGoal || '',
    tone: data.tone || '',
    username: data.username !== undefined ? data.username : '',
    qna: qnaValue,
    knowledge: Array.isArray(data.knowledge) ? data.knowledge : [],
    media: Array.isArray(data.media) ? data.media : [],
    links: Array.isArray(data.links) ? data.links : [],
    showcases: fixedShowcases,
    spotlight: fixedSpotlight,
    chatPrompts: Array.isArray(data.chatPrompts) ? data.chatPrompts : []
  };
};

export const useProfileData = (user, isLoaded, setIsLoaded, showToast) => {
  const location = useLocation();
  const [profileData, setProfileData] = useState(defaultProfileData);
  const [isLoadingFromFirestore, setIsLoadingFromFirestore] = useState(false);
  const lastDataHashRef = useRef(null);
  const lastSavedQnaRef = useRef(null);
  const lastSaveTimeRef = useRef(0);
  const [usernameError, setUsernameError] = useState(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [originalUsername, setOriginalUsername] = useState(null);
  const usernameCheckTimeoutRef = useRef(null);
  const [savingUsername, setSavingUsername] = useState(false);
  
  // Determine if we're in editor/preview mode (where real-time sync is needed)
  const isEditorMode = location.pathname === '/editor' || location.pathname === '/preview';

  // Fetch Data from Firestore (Once user is auth'd) - OPTIMIZED FOR FAST INITIAL LOAD
  useEffect(() => {
    if (!user) {
      setIsLoaded(true);
      return;
    }

    setIsLoaded(true);

    const docRef = doc(db, 'artifacts', appId, 'users', user.uid);
    let unsubscribeSnapshot = null;

    // Step 1: Fast initial load with getDoc
    getDoc(docRef).then(async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        // Optimized: Use fast hash instead of JSON.stringify for initial comparison
        const dataHash = fastHash(data);
        lastDataHashRef.current = dataHash;
        
        setIsLoadingFromFirestore(true);
        const mergedData = mergeProfileData(data);
        setProfileData(prev => mergedData);
        
        if (data.username && data.username.trim() !== '') {
          setOriginalUsername(data.username);
        } else {
          setOriginalUsername(null);
        }
        
        lastSavedQnaRef.current = JSON.stringify(data.qna || []);
        // Optimized: Reduce delay and use single timeout
        setTimeout(() => setIsLoadingFromFirestore(false), 800);
      } else {
        // New user - pre-populate with Google account info if available
        const isGoogleUser = user.providerData?.some(provider => provider.providerId === 'google.com');
        const pendingUsername = new URLSearchParams(window.location.search).get('username') || '';
        const initialData = {
          ...defaultProfileData,
          username: pendingUsername,
          ...(isGoogleUser && user.displayName && { name: user.displayName }),
          ...(isGoogleUser && user.photoURL && { avatar: user.photoURL })
        };
        
        if (isGoogleUser && user.email) {
          initialData.contactInfo = {
            phones: [],
            emails: [{ email: user.email, type: 'personal' }]
          };
        }
        
        setProfileData(initialData);
        setOriginalUsername(null);
        lastSavedQnaRef.current = JSON.stringify(initialData.qna || []);
        setIsLoadingFromFirestore(false);
        
        // Create minimal document for new users
        try {
          await setDoc(doc(db, 'artifacts', appId, 'users', user.uid), {
            createdAt: serverTimestamp(),
            isNewUser: true
          }, { merge: true });
        } catch (err) {
          console.error('Error creating initial profile document:', err);
        }
      }
      
      // Step 2: Set up real-time listener if in editor/preview mode
      // Optimized: Remove unnecessary 3-second delay - set up listener immediately after initial load
      if (isEditorMode) {
        // Set up listener immediately after initial data is loaded
        unsubscribeSnapshot = onSnapshot(docRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();
            // Optimized: Use fast hash instead of JSON.stringify for comparison
            const dataHash = fastHash(data);
            
            // Skip if hash matches (data hasn't changed)
            if (dataHash === lastDataHashRef.current) {
              return;
            }
            
            // Skip if we just saved (within last 2 seconds) to prevent unnecessary updates
            const timeSinceLastSave = Date.now() - lastSaveTimeRef.current;
            if (timeSinceLastSave < 2000) {
              return;
            }
            
            lastDataHashRef.current = dataHash;
            
            // Only show loading state if significant time has passed since last update
            const shouldShowLoading = timeSinceLastSave > 5000;
            if (shouldShowLoading) {
              setIsLoadingFromFirestore(true);
            }
            
            // Use extracted merge function to avoid duplication
            const mergedData = mergeProfileData(data);
            // Batch state updates - React 18+ auto-batches, but we ensure atomicity
            setProfileData(prev => mergedData);
            setOriginalUsername(data.username && data.username.trim() !== '' ? data.username : null);
            
            lastSavedQnaRef.current = JSON.stringify(data.qna || []);
            
            // Only delay hiding loading if we showed it
            if (shouldShowLoading) {
              setTimeout(() => setIsLoadingFromFirestore(false), 500);
            }
          }
        }, (error) => {
          console.error("Error in real-time listener:", error);
        });
      }
    }).catch((error) => {
      console.error("Error fetching data:", error);
      setIsLoadingFromFirestore(false);
    });

    return () => {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
      if (usernameCheckTimeoutRef.current) {
        clearTimeout(usernameCheckTimeoutRef.current);
      }
      lastDataHashRef.current = null;
    };
  }, [user, isEditorMode, setIsLoaded]);

  // Check if username is available
  const checkUsernameAvailability = async (username, currentUserId) => {
    if (!username || username.trim() === '') {
      return { available: false, error: 'Username cannot be empty' };
    }

    const reservedRoutes = ['auth', 'editor', 'preview', 'profile', 'privacy', 'terms', 'comparison', 'blog'];
    if (reservedRoutes.includes(username.toLowerCase())) {
      return { available: false, error: 'This username is reserved' };
    }

    try {
      const usersRef = collection(db, 'artifacts', appId, 'users');
      const q = query(usersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { available: true, error: null };
      }
      
      const existingDoc = querySnapshot.docs[0];
      if (existingDoc.id === currentUserId) {
        return { available: true, error: null };
      }
      
      return { available: false, error: 'This username is already taken' };
    } catch (error) {
      console.error('Error checking username:', error);
      return { available: false, error: 'Error checking username availability' };
    }
  };

  // Manual save function for username
  const saveUsername = async () => {
    if (!user || !profileData.username) {
      if (showToast) showToast('Please enter a username first', 'error');
      return;
    }

    if (profileData.username === originalUsername && originalUsername) {
      if (showToast) showToast('Username unchanged', 'info');
      return;
    }

    setSavingUsername(true);
    setUsernameError(null);

    try {
      const validation = await checkUsernameAvailability(profileData.username, user.uid);
      
      if (!validation.available) {
        setUsernameError(validation.error);
        setSavingUsername(false);
        if (showToast) showToast(validation.error, 'error');
        return;
      }

      await setDoc(doc(db, 'artifacts', appId, 'users', user.uid), { username: profileData.username }, { merge: true });
      setOriginalUsername(profileData.username);
      
      if (showToast) {
        showToast(`Username updated to @${profileData.username}`, 'success');
      }
    } catch (err) {
      console.error('Error saving username:', err);
      setUsernameError('Failed to save username. Please try again.');
      if (showToast) {
        showToast('Failed to save username. Please try again.', 'error');
      }
    } finally {
      setSavingUsername(false);
    }
  };

  return {
    profileData,
    setProfileData,
    isLoadingFromFirestore,
    usernameError,
    setUsernameError,
    isCheckingUsername,
    setIsCheckingUsername,
    originalUsername,
    usernameCheckTimeoutRef,
    savingUsername,
    checkUsernameAvailability,
    saveUsername,
    lastSavedQnaRef,
    lastSaveTimeRef
  };
};

